import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import { Close } from '@material-ui/icons';
import { Checkbox, FormControlLabel, IconButton, TextField } from '@material-ui/core';

import FormTextField from '../../components/DynamicForm/FormTextField';
import { generateUUID } from '../../utils/commonUtil';

import './AddOrUpdateField.scss';
import { FieldData } from './AddOrUpdateField';

interface AddChoiceFieldProps {
    data?: FieldData;
    isMultiChoice?: boolean;
    onChange: (_field: any) => void;
};

const AddChoiceField = ({ data, isMultiChoice = false, onChange }: AddChoiceFieldProps) => {
    const { t } = useTranslation();

    const [inputFieldName, setInputFieldName] = useState<string>('');
    const [inputFieldOptions, setInputFieldOptions] = useState<string[]>(['']);
    const [isRequired, setIsRequired] = useState<boolean>(false);

    const toggleIsRequired = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsRequired(event.target.checked);
    };

    const handleOptionChange = (index: number, value: string) => {
        const updatedFields = [...inputFieldOptions];
        updatedFields[index] = value;
        setInputFieldOptions(updatedFields);
    };

    const addInputFieldOption = () => {
        setInputFieldOptions([...inputFieldOptions, '']);
    };

    const removeInputFieldOption = (index: number) => {
        setInputFieldOptions(inputFieldOptions.filter((_, i) => i !== index));
    };


    const fieldData = useMemo(() => {
        return {
            name: inputFieldName,
            key: generateUUID().replace(/-/g, ''),
            options: inputFieldOptions.filter(Boolean),
            label: true,
            isRequired,
        }
    }, [inputFieldName, isRequired, inputFieldOptions]);

    useEffect(() => {
        if (data) {
            setInputFieldName(data.title);
            setIsRequired(data.isRequired);
            setInputFieldOptions(data.options || ['']);
            onChange(fieldData);
        }
    }, [data, isMultiChoice]);

    useEffect(() => {
        if (inputFieldName && inputFieldOptions.filter(Boolean).length) {
            onChange(fieldData);
        } else {
            onChange(null);
        }
    }, [isRequired, inputFieldName, inputFieldOptions])

    const label = isMultiChoice ? t('CREATE_OR_UPDATE_FORM_MULTIPLE_CHOICE') : t('CREATE_OR_UPDATE_FORM_SINGLE_CHOICE');

    return (
        <div className="addFieldFormContainer">
            <div>{label}</div>
            <FormTextField
                id="new-input-field"
                title={t('CREATE_OR_UPDATE_FORM_FIELD_LABEL')}
                placeholder={true}
                value={inputFieldName}
                onChange={(_, val) => setInputFieldName(val)}
            />
            <div>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isRequired}
                            onChange={toggleIsRequired}
                            color="primary"
                        />
                    }
                    label={t('CREATE_OR_UPDATE_FORM_FIELD_IS_REQUIRED')}
                />
            </div>
            <div className="addFieldFormOptionTitle">{label} {t('COMMON_SETTINGS')}</div>
            {inputFieldOptions.map((value, index) => (
                <div key={index} className="addFieldFormOption">
                    <div className="inputContainer">
                        <TextField
                            key={index}
                            variant="standard"
                            label=""
                            value={value}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            placeholder={t('COMMON_CHOICE')}
                            fullWidth
                        />
                    </div>
                    {inputFieldOptions.length === index + 1 ?
                        <Button
                            key={index}
                            type="button"
                            variant="outlined"
                            onClick={addInputFieldOption}>
                            {t('COMMON_ADD')}
                        </Button>
                        :
                        <IconButton onClick={() => removeInputFieldOption(index)}>
                            <Close />
                        </IconButton>
                    }
                </div>
            ))}
        </div>
    );
};

export default AddChoiceField;
